import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Button, Input } from '../../ui/index.js'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { usePageTitle } from '../../lib/page-title.js'
import { AuthLayout } from './AuthLayout.js'
import { GoogleButton } from './GoogleButton.js'
import { FieldError, FormError } from './FormError.js'


export function Login() {
  usePageTitle('Log in')
  const { api, signIn } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)

    const next: Record<string, string> = {}
    if (password === '') next['password'] = 'Enter your password'
    if (email.trim() === '' && password === '') next['email'] = 'Enter your email address'
    setErrors(next)
    if (Object.keys(next).length > 0) return

    setBusy(true)
    try {
      const result = await api.post<{ accessToken: string; refreshToken: string }>('/api/auth/login', {
        email: email.trim(),
        password,
        remember,
      })
      await signIn(result)
      navigate('/', { replace: true })
    } catch (error) {
      if (error instanceof ApiError && error.isValidation && error.details !== undefined) {
        setErrors(error.details)
      } else if (error instanceof ApiError) {
        setFormError(error.message)
      } else {
        setFormError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your OVRSEE workspace."
      footer={
        <>
          New to OVRSEE? <Link to="/signup">Create an account</Link>
        </>
      }
    >
      <GoogleButton intent="signin" />
      <form onSubmit={submit} noValidate>
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="email">
            Work email
          </label>
          <Input
            id="email"
            name="email"
            type="text"
            autoComplete="email"
            value={email}
            onChange={setEmail}
          />
          <FieldError message={errors['email']} />
        </div>
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <label className="field-label" htmlFor="password">
              Password
            </label>
            <Link to="/forgot-password" style={{ fontSize: 12 }}>
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
          <FieldError message={errors['password']} />
        </div>
        <label
          htmlFor="remember"
          style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 20px', fontSize: 13, cursor: 'pointer' }}
        >
          <input
            id="remember"
            name="remember"
            type="checkbox"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
            style={{ accentColor: 'var(--brand)', width: 15, height: 15 }}
          />
          Keep me signed in for 30 days
        </label>
        <FormError message={formError} />
        <Button type="submit" kind="primary" disabled={busy} style={{ width: '100%' }}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  )
}
